/*
 *  Collection Template
 *    Contains all the scripts used for the collection template.
 *
 *  Version:
 *    1.0.0 - 2022/06/30
 */

import $ from 'jquery';
import { AjaxCollectionTemplate, PriceRangeFilter, PriceSlideFacet, LoadMore, SelectSorter, TagFilter, VariantOptionFilter, OPERATION_OR, SmartTagButtonFacet, VO_MODE_VARIANT } from '@process-creative/slate-ajax-collection';
import { t } from '@process-creative/slate-theme-tools';
import { ProductThumbnailPrint } from '../../objects/product/product-thumbnail-print';
import {  CLASS_ACTIVE } from '../../tools/constants/classes';
import { CustomSelectSorter } from './sort/CustomSelectSort';
import { CustomTagCheckboxFacet } from './facets/CustomTagCheckboxFacet';
import { AvailabilityFilter } from './filters/AvailabilityFilter';
import { AvailabilityFacet } from './facets/AvailabilityFacet';
import { CustomVariantOptionFacet } from './facets/CustomVariantOptionFacet';
import { CustomPriceSlideFacet } from './facets/CustomPriceSliderFacet';
import * as noUiSlider from 'nouislider';
import FilterUtils from '../../utils/FilterUtils';

import { documentDisableScroll, documentEnableScroll } from "../../tools/document";
import { rebindWishlist } from "../../objects/swym-wishlist";
import { responsiveMediaAbove } from "../../settings/responsive";


// Selectors
const SELECTOR_COLLECTION_TEMPLATE = '[data-collection-template]';
const SELECTOR_FACETS_FORM = '[data-facets-form]';
const SELECTOR_RESULTS = '[data-collection-results]';
const SELECTOR_FILTER_SORT = '[data-filter-sort]';
const SELECTOR_PAGINATION_BTN = '[data-pagination-button]';
const SELECTOR_NO_RESULTS = '[data-collection-no-results]';
const SELECTOR_PAGINATION_COUNT = '[data-pagination-count]';
const SELECTOR_PAGINATION_PROGRESS = '[data-pagination-progress]';
const SELECTOR_SORT = '[data-sort]';
const SELECTOR_FACET_AVAILABILITY = '[data-faceted-nav-available]';
// const SELECTOR_FACET_COLOUR = '[data-faceted-nav-colour]';
const SELECTOR_FACETED_NAV_MATERIAL = '[data-faceted-nav-material]';
const SELECTOR_FACETED_NAV_STYLE = '[data-faceted-nav-style]';
const SELECTOR_FACET_PRICE = '[data-faceted-nav-price]';
const SELECTOR_FACETED_NAV_COLOUR = '[data-faceted-nav-colour]';
const SELECTOR_FACETED_NAV_SIZE = '[data-faceted-nav-size]';
const SELECTOR_FACETED_NAV_AVAILABILITY = '[data-faceted-nav-availability]';
const SELECTOR_FACETED_NAV_SORT = '[data-faceted-nav-sort]';

const SELECTOR_FACETS_VIEW_BTN = '[data-facets-view-btn]';
const SELECTOR_DRAWER_TRIGGER =        '[data-filters-trigger]';
const SELECTOR_DRAWER =                '[data-filters-drawer]';
const SELECTOR_DRAWER_CLOSE =          '[data-filters-drawer-close]';
const SELECTOR_FACETS =                '.c-facets';
const SELECTOR_FOOTER =                '[data-footer]';

const CLASS_CB_VISIBLE = 'is-cb-visible';
const CLASS_FACETS_HIDDEN = 'is-footer-reached';

//Attributes
const ATTR_CONTENT_BLOCK_POSITION = 'data-content-block-position';

export const CACHE_DATE_KEY = 'AjaxCollectionProductCacheTime';
export const CACHE_DATA_KEY = 'AjaxCollectionProductCacheData';


export class CollectionTemplate extends AjaxCollectionTemplate {
  constructor(container) {
    super(container);
    this.container = container;

    this.setupCollection();
    this.init();
  }

  setupCollection() {
    try {
      localStorage.removeItem(CACHE_DATA_KEY);
      localStorage.removeItem(CACHE_DATE_KEY);
      this.init();
    } catch(e) {
      // Cache clearing failed - continue anyway
    }

    this.setElements();
    this.sortSelect = this.container.find(SELECTOR_FILTER_SORT);
    this.facetsForm = this.container.find(SELECTOR_FACETS_FORM)[0];
    this.results = this.container.find(SELECTOR_RESULTS);
    this.draw.setDisplayMode('FirstAvailableVariant');
  }

  /**
   * Initialize all collection filters, facets, and UI interactions
   * Sets up price sliders, tag filters, variant filters, and drawer behavior
   */
  setupFilters() {
    this.drawer = this.container.find(SELECTOR_DRAWER);
    this.container.on('click', SELECTOR_DRAWER_TRIGGER, () => {
      this.drawer.addClass(CLASS_ACTIVE);
      documentDisableScroll();
    });

    this.container.on('click', SELECTOR_DRAWER_CLOSE, () => {
      this.drawer.removeClass(CLASS_ACTIVE);
      documentEnableScroll();
    });

    // Setup tab functionality for desktop layout
    this.setupFacetTabs();

    // Setup sorting elements
    this.setupSortElements();
    
    // Read dynamic filter configuration from Liquid
    const dynamicFiltersConfig = this.getDynamicFiltersConfig();
    
    // Initialize dynamic filters storage
    this.dynamicFilters = [];
    this.dynamicFacets = [];
    
    // Setup Static Filters (Price, Size, Availability)
    const staticFilters = [
      this.priceFilter = new PriceRangeFilter(this, 'Price'),
      this.sizeFilter = new VariantOptionFilter(this, 'Size', OPERATION_OR, ['Size', 'size', 'SIZE']),
      this.availabilityFilter = new AvailabilityFilter(this)
    ];
    
    // Setup Dynamic Filters from configuration
    dynamicFiltersConfig.forEach(filterName => {
      const filterKey = `${filterName.toLowerCase()}Filter`;
      this[filterKey] = new TagFilter(this, filterName, OPERATION_OR);
      this.dynamicFilters.push({
        key: filterKey,
        name: filterName,
        filter: this[filterKey]
      });
      staticFilters.push(this[filterKey]);
    });
    
    // Add all filters to the collection
    staticFilters.forEach(filter => this.filters.addFilter(filter));

    // Setup Static Facets
    const staticFacets = [
      // Price sliders for mobile and desktop
      (this.priceFilterFacetMobile = new CustomPriceSlideFacet({
        template: this,
        filter: this.priceFilter,
        title: 'Price',
        container: '[data-price-mobile]',
        noUiSlider
      })),
      (this.priceFilterFacetDesktop = new CustomPriceSlideFacet({
        template: this,
        filter: this.priceFilter,
        title: 'Price',
        container: '[data-price-desktop]',
        noUiSlider
      })),
      // Size facet
      (this.sizeFacet = new CustomVariantOptionFacet({
        template: this,
        filter: this.sizeFilter,
        title: 'Size',
        container: SELECTOR_FACETED_NAV_SIZE,
        optionNames: ['Size']
      })),
      // Availability facet
      this.availabilityFacet = new AvailabilityFacet({
        template: this, 
        filter: this.availabilityFilter,
        title: 'Availability',
        container: SELECTOR_FACETED_NAV_AVAILABILITY
      })
    ];
    
    // Setup Dynamic Facets
    this.dynamicFilters.forEach(({ key, name, filter }) => {
      const facetKey = key.replace('Filter', 'Facet');
      const containerSelector = `[data-faceted-nav-${name.toLowerCase()}]`;
      
      // Determine title - use translation for Colour, otherwise use name
      const title = name.toLowerCase() === 'colour' 
        ? t('collections.collection.filter_color') 
        : name;
      
      this[facetKey] = new CustomTagCheckboxFacet({
        template: this,
        filter: filter,
        title: title,
        container: containerSelector,
        prefixes: [name],
        token: '~'
      });
      
      this.dynamicFacets.push({
        key: facetKey,
        name: name,
        facet: this[facetKey]
      });
      
      staticFacets.push(this[facetKey]);
    });
    
    // Add all facets to the collection
    staticFacets.forEach((facet) => this.filters.addFacet(facet));

    // Synchronize the two price sliders
    this.synchronizePriceSliders();

    // Setup mobile accordion behavior
    this.setupMobileAccordions();

    // Setup filter count tracking
    this.setupFilterTracking();

    // Initialize drawer close button with initial product count
    setTimeout(() => {
      // Initially no filters are active, so pass 0 to trigger unpaginated count display
      this.updateDrawerCloseButtonText(0);
    }, 100);

    // Setup mobile facets hide behavior when reaching footer
    this.setupMobileFacetsFooterHide();

    // Slider initialization is handled by CustomPriceSlideFacet
    // which automatically calculates min/max values from product prices
  }

  /**
   * Get dynamic filter configuration from Liquid data attribute
   * Parses, validates, and returns an array of filter names
   * @returns {Array} Array of filter names (e.g., ['Material', 'Colour', 'Style'])
   */
  getDynamicFiltersConfig() {
    const facetsForm = this.container.find(SELECTOR_FACETS_FORM);
    if (!facetsForm.length) {
      console.warn('CollectionTemplate: Facets form not found, using default filters');
      return ['Material', 'Colour', 'Style'];
    }
    
    const configString = facetsForm.attr('data-dynamic-filters');
    if (!configString || configString.trim() === '') {
      console.warn('CollectionTemplate: No dynamic filters configuration found, using defaults');
      return ['Material', 'Colour', 'Style'];
    }
    
    // Parse comma-separated string
    const filters = configString.split(',')
      .map(f => f.trim())
      .filter(f => f !== ''); // Remove empty values
    
    // Remove duplicates (case-insensitive)
    const uniqueFilters = [];
    const seen = new Set();
    
    filters.forEach(filter => {
      const lowerFilter = filter.toLowerCase();
      if (!seen.has(lowerFilter)) {
        seen.add(lowerFilter);
        uniqueFilters.push(filter);
      }
    });
    
    if (uniqueFilters.length === 0) {
      console.warn('CollectionTemplate: No valid filters after parsing, using defaults');
      return ['Material', 'Colour', 'Style'];
    }
    
    return uniqueFilters;
  }

  /**
   * Sync mobile and desktop price sliders so they update together
   * When one slider changes, the other automatically follows
   */
  synchronizePriceSliders() {
    // Add cross-synchronization between mobile and desktop price sliders
    if (this.priceFilterFacetMobile && this.priceFilterFacetDesktop) {
      const mobileSlider = this.priceFilterFacetMobile.rangeElement?.noUiSlider;
      const desktopSlider = this.priceFilterFacetDesktop.rangeElement?.noUiSlider;
      
      if (mobileSlider && desktopSlider) {
        FilterUtils.synchronizeSliders(mobileSlider, desktopSlider, (values) => {
          // Update price displays for both sliders
          this.priceFilterFacetMobile.updatePriceDisplay(values);
          this.priceFilterFacetDesktop.updatePriceDisplay(values);
        });
      }
    }
  }

  /**
   * Set up mobile accordion behavior - only one accordion open at a time
   * When opening an accordion, automatically close others
   */
  setupMobileAccordions() {
    const mobileAccordionContainer = this.container.find('[data-mobile-accordions]');
    if (!mobileAccordionContainer.length) return;

    const accordionItems = mobileAccordionContainer.find('details.c-facets__accordion-item');
    
    // Add click handler to each accordion summary
    accordionItems.each((index, accordion) => {
      const $accordion = $(accordion);
      const $summary = $accordion.find('summary');
      
      $summary.on('click', (e) => {
        // Get the current state before the default behavior changes it
        const wasOpen = $accordion.prop('open');
        
        // Small delay to let the default behavior happen first
        setTimeout(() => {
          // If this accordion was just opened, close all others
          if (!wasOpen && $accordion.prop('open')) {
            accordionItems.each((i, otherAccordion) => {
              const $otherAccordion = $(otherAccordion);
              if (otherAccordion !== accordion && $otherAccordion.prop('open')) {
                $otherAccordion.prop('open', false);
              }
            });
          }
        }, 10);
      });
    });
  }

  updateFilterCount() {
    const filterTrigger = this.container.find(SELECTOR_DRAWER_TRIGGER);
    if (!filterTrigger.length) return;

    // Build filter configs dynamically
    const filterConfigs = [
      { filter: this.priceFilter, type: 'price' },
      { filter: this.sizeFilter, type: 'default' },
      { filter: this.availabilityFilter, type: 'availability' }
    ];
    
    // Add dynamic filters
    if (this.dynamicFilters && this.dynamicFilters.length > 0) {
      this.dynamicFilters.forEach(({ filter }) => {
        filterConfigs.push({ filter: filter, type: 'default' });
      });
    }

    const activeFilterCount = FilterUtils.countActiveFilters(filterConfigs);
    FilterUtils.updateFilterCountDisplay(filterTrigger, activeFilterCount);
  }

  setupFilterTracking() {
    // Set up filter change tracking with debounced updates
    FilterUtils.setupFilterTracking(this.filters, () => this.updateFilterCount(), 100);

    // Also track form submissions and resets
    if (this.facetsForm) {
      $(this.facetsForm).on('submit', () => {
        setTimeout(() => this.updateFilterCount(), 100);
      });
    }

    // Initial count update
    setTimeout(() => this.updateFilterCount(), 500);
  }

  setupFacetTabs() {
    const facetTabs = this.container.find('[data-facet-tab]');
    const facetContents = this.container.find('[data-facet-content]');
    
    facetTabs.on('click', (e) => {
      e.preventDefault();
      const tabIndex = $(e.currentTarget).data('facet-tab');
      
      // Remove active class from all tabs and contents
      facetTabs.removeClass('active');
      facetContents.removeClass('active');
      
      // Add active class to clicked tab and corresponding content
      $(e.currentTarget).addClass('active');
      this.container.find(`[data-facet-content='${tabIndex}']`).addClass('active');
    });
  }

  setupSortElements() {
    const sortMethods = Object.values(this.sort.getSortMethods()).filter(arrayItem => typeof arrayItem == 'object');
    
    // Setup the main sort dropdown (if it exists)
    const mainSortElems = this.container[0].querySelectorAll(SELECTOR_FILTER_SORT);
    mainSortElems.forEach(sortElem => {
      this.sort.addSorter(new SelectSorter(this, $(sortElem)));
    });
    
    // Setup the facet sort checkboxes
    const facetSortElems = this.container[0].querySelectorAll(SELECTOR_FACETED_NAV_SORT);
    facetSortElems.forEach(sortElem => {
      this.sort.addSorter(new CustomSelectSorter({
        template: this,
        sortElem,
        sortMethods
      }));
    });
  }

  setElements() {
    // Elements
    // this.count = this.container.find(SELECTOR_PRODUCTS_COUNT);
    this.noResults = this.container.find(SELECTOR_NO_RESULTS);

    // Pagination
     this.pagination.setPaginator(
      new LoadMore(this, this.container.find(SELECTOR_PAGINATION_BTN))
    );

    //
    this.setupFilters();
  }


  generatePrint(v) {
    const p = v.product;
    const p2 = { ...p }; // Duplicate
    p2.variants = p2.variants.map(variant => {
      const v2 = { ...variant }; // Duplicate
      delete v2.product; // Remove circular reference
      return v2;
    });

    const draw = window.collection?.draw;

    if (draw && draw.drawnProducts) {
      const length = draw.drawnProducts.length;
      const productCount = document.querySelector(SELECTOR_PAGINATION_COUNT);
      if (productCount) {
        productCount.textContent = length;
      }

      const paginationProgress = document.querySelector(SELECTOR_PAGINATION_PROGRESS);
      const minValue = length;
      const maxValue = draw.unpaginatedProductCount;

      if (paginationProgress && typeof maxValue === 'number' && maxValue > 24) {
        const scaledValue = (minValue / maxValue) * 100;
        paginationProgress.style.width = Math.floor(scaledValue) + '%';

        if(draw.drawnProducts.length == maxValue) {
          document.querySelector(SELECTOR_PAGINATION_BTN).style.display = 'none';
        }
      }

    } 
    return ProductThumbnailPrint({ p, p2, v, clazz: 'c-collection__item' });
  }

  getContentBlockSize(params) {
    return 4;
  }

  // This function here is crucial to make sure the content blocks are counted properly
  // in perPage and to calculate the pagination
  getContentBlocksForPage(params) {
    try {
      const contentBlocks = params.contentBlocks.filter(c => this.contentblockIsOnPage($(c), params.perPage, params.page));
      if (contentBlocks.length) return contentBlocks;

      return -1;
    } catch (e) {
      console.error(e);
      return -1;
    }
  }

  contentblockIsOnPage(contentBlock, perPage, currentPage) {
    const blockPosition = contentBlock.attr('data-content-block-position') || 999;
    // Load More DM requires CB to be visible after page 1

    return blockPosition < currentPage * perPage;
  }

  getContentBlockPosition(cb, thumbnails) {
    if (thumbnails.length <= 0) return -1; // No thumbs? hide

    let cbAttr = cb.attr('data-content-block-position');
    if (this.contentblockIsOnPage(cb, this.pagination.template.perPage, this.pagination.getCurrentPage())) {
      // only show content block if it fits with the amount of thumbnails
      if (cbAttr <= thumbnails.length) {
        cb.addClass(CLASS_CB_VISIBLE);
        cb.show();
      } else {
        cb.hide();
        return;
      }
    }
    return cb.attr(ATTR_CONTENT_BLOCK_POSITION);
  }

  onVariantsDrawn(variants) {
    const unpaginatedProductCount = this.draw.unpaginatedProductCount;
    if(unpaginatedProductCount != '' && unpaginatedProductCount != undefined) {
      // display number of products visible in the page count
      const message = t('collections.collection.results_text_html', {
        count: variants.length
      });
      // this.count.html(message);
    }

    // Update the filters drawer close button with current result count
    this.updateDrawerCloseButtonText(variants.length);
    // Rebind wishlist functionality for the new content
    rebindWishlist();

    // initialiseProductThumbnails();
  }

  /**
   * Update the filter drawer's "View X items" button text
   * Shows filtered count if filters active, total count if no filters
   */
  updateDrawerCloseButtonText(resultCount) {
    const drawerViewBtn = document.querySelector(SELECTOR_FACETS_VIEW_BTN);
    if (drawerViewBtn) {
      // Check if there are any active filters
      const hasActiveFilters = this.hasActiveFilters();
      
      // If there are active filters, show the filtered result count
      // If no active filters, show the total unpaginated product count
      const count = hasActiveFilters ? resultCount : (this.draw?.unpaginatedProductCount || 0);
      
      const itemText = count === 1 ? t('collections.collection.item') : t('collections.collection.items');
      drawerViewBtn.textContent = `${t('collections.collection.view')} ${count} ${itemText}`;
    }
  }

  hasActiveFilters() {
    const filters = {
      priceFilter: this.priceFilter,
      sizeFilter: this.sizeFilter,
      availabilityFilter: this.availabilityFilter
    };
    
    // Add dynamic filters
    if (this.dynamicFilters && this.dynamicFilters.length > 0) {
      this.dynamicFilters.forEach(({ key, filter }) => {
        filters[key] = filter;
      });
    }

    return FilterUtils.hasAnyActiveFilters(filters);
  }

  /**
   * Setup mobile facets hide behavior when footer is reached
   * Hides the fixed facets bar on mobile when scrolling to the footer
   */
  setupMobileFacetsFooterHide() {
    const facetsElement = this.container.find(SELECTOR_FACETS);
    const footerElement = $(SELECTOR_FOOTER);

    if (!facetsElement.length || !footerElement.length) return;

    // Only apply on mobile (below medium breakpoint)
    const checkFooterIntersection = () => {
      if (responsiveMediaAbove('medium')) {
        // On desktop, remove the hidden class if it exists
        facetsElement.removeClass(CLASS_FACETS_HIDDEN);
        return;
      }

      // Don't hide if drawer is active
      if (this.drawer && this.drawer.hasClass(CLASS_ACTIVE)) {
        facetsElement.removeClass(CLASS_FACETS_HIDDEN);
        return;
      }

      // On mobile, check if footer is in view
      const footerOffset = footerElement.offset();
      if (!footerOffset) return; // Footer not found, skip

      const footerTop = footerOffset.top;
      const windowBottom = $(window).scrollTop() + $(window).height();
      const facetsHeight = facetsElement.outerHeight() || 0;

      // Hide facets when footer is within viewport (accounting for facets height)
      if (windowBottom >= footerTop - facetsHeight) {
        facetsElement.addClass(CLASS_FACETS_HIDDEN);
      } else {
        facetsElement.removeClass(CLASS_FACETS_HIDDEN);
      }
    };

    // Check on scroll
    $(window).on('scroll.mobileFacetsHide', checkFooterIntersection);
    
    // Check on resize (in case breakpoint changes)
    $(window).on('resize.mobileFacetsHide', checkFooterIntersection);
    
    // Check when drawer opens/closes
    if (this.drawer) {
      this.container.on('click', SELECTOR_DRAWER_TRIGGER, () => {
        setTimeout(checkFooterIntersection, 100);
      });
      this.container.on('click', SELECTOR_DRAWER_CLOSE, () => {
        setTimeout(checkFooterIntersection, 100);
      });
    }
    
    // Initial check
    checkFooterIntersection();
  }

}

$(() => {
  const container = $(SELECTOR_COLLECTION_TEMPLATE);

  if (!container || !container.length) return;
  if (container.attr('data-initialized')) return;

  window.collection = new CollectionTemplate(container);
});